import { ReactNode, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import TextWinds from './TextWinds';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface FormDialogProps {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** 하단에 고정할 액션 영역. 본문이 길어져도 항상 보인다. */
  footer?: ReactNode;
}

/**
 * @description
 * - 화면 중앙에 띄우는 팝업
 * - PC/모바일 모두 같은 형태를 쓴다.
 * - 스크롤 잠금, 포커스 트랩, ESC/배경 클릭 닫기를 직접 처리한다.
 */
const FormDialog = ({
  open,
  title,
  onClose,
  children,
  footer,
}: FormDialogProps) => {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    // 열려있는 동안 배경 스크롤을 막는다.
    const prevBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // 닫힐 때 원래 포커스가 있던 요소로 되돌린다.
    const previouslyFocused = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }

      if (event.key !== 'Tab' || !panelRef.current) return;

      // 포커스가 팝업 밖으로 나가지 않도록 Tab 순환을 가둔다.
      const focusable = panelRef.current.querySelectorAll<HTMLElement>(
        FOCUSABLE_SELECTOR,
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = prevBodyOverflow;
      window.removeEventListener('keydown', handleKeyDown);
      previouslyFocused?.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    /**
     * 배경(backdrop)은 `position: fixed; inset: 0` 인 오버레이라 h-full/w-full 로 잡아도
     * 뷰포트가 아니라 실제로 보이는 영역 기준으로 맞는다. 모바일 브라우저의 100vh 는
     * 주소창/툴바를 감춘 높이라 실제 화면보다 커서, 이 값을 쓰면 가운데 정렬한 팝업이
     * 화면 아래로 밀려 버튼이 툴바에 가려지거나 잘린다.
     *
     * flex 는 기본 flex-shrink: 1 이라 남는 공간이 없으면 폭을 실제로 줄이므로,
     * 팝업의 max-w-full 이 뷰포트 기준으로 정상 동작한다. (grid 는 auto 트랙이
     * 팝업의 max-content 폭으로 잡혀 좁은 화면에서 오른쪽이 잘려나간다.)
     */
    <div
      className="fixed inset-0 z-[9999] flex h-full w-full items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className="m-0 flex max-h-full w-[520px] min-w-0 max-w-full flex-col overflow-hidden rounded-xl bg-white font-sans text-base font-light leading-relaxed text-blue-gray-500 shadow-2xl antialiased outline-none"
      >
        <header className="flex shrink-0 items-center justify-between border-b border-gray3 px-5 py-4">
          <TextWinds type="title_h3">{title}</TextWinds>
          <button
            type="button"
            onClick={onClose}
            aria-label="닫기"
            className="flex h-8 max-h-8 w-8 max-w-8 shrink-0 items-center justify-center rounded-full hover:bg-gray1"
          >
            <XMarkIcon className="h-6 w-6 text-gray8" />
          </button>
        </header>

        {/* 본문만 스크롤되고 헤더/푸터는 항상 보인다. */}
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {children}
        </div>

        {footer && (
          <footer className="shrink-0 border-t border-gray2 px-5 py-4">
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default FormDialog;
